import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useLanguage, pick } from "@/lib/lang";
import { t } from "@/lib/content";

const STORAGE_KEY = "patrice.ribbon.dismissed";
const REVEAL_AT = 0.55; // 55% of the page

const BookingRibbon = () => {
  const { lang } = useLanguage();
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Hide on the contact page itself (already there)
  const hiddenRoutes = ["/kontakt", "/contact", "/impressum", "/datenschutz"];
  const isHiddenRoute = hiddenRoutes.includes(location.pathname);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.localStorage.getItem(STORAGE_KEY) === "1") {
      setDismissed(true);
      return;
    }
    const onScroll = () => {
      const h = document.documentElement;
      const denom = (h.scrollHeight - h.clientHeight) || 1;
      const pct = h.scrollTop / denom;
      setVisible(pct > REVEAL_AT);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [location.pathname]);

  const dismiss = () => {
    setDismissed(true);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, "1");
    }
  };

  if (dismissed || isHiddenRoute) return null;

  const show = visible;

  return (
    <div
      aria-hidden={!show}
      data-testid="booking-ribbon"
      className={[
        "fixed left-1/2 -translate-x-1/2 z-40 transition-all duration-700",
        "bottom-5 md:bottom-7",
        show ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 translate-y-6 pointer-events-none",
      ].join(" ")}
    >
      <div className="relative flex items-stretch bg-depth text-canvas shadow-[0_18px_45px_rgba(40,0,27,0.25)] border border-warmth/20 rounded-full overflow-hidden">
        <div className="flex items-center pl-5 md:pl-7 pr-3 md:pr-5 py-2 md:py-2.5 gap-3 md:gap-4">
          <span className="hidden sm:inline-block w-2 h-2 rounded-full bg-signal animate-pulse" aria-hidden />
          <p className="font-display italic text-base md:text-lg leading-none whitespace-nowrap">
            {lang === "de" ? "Mit Patrice arbeiten" : "Work with Patrice"}
          </p>
          <span className="hidden md:inline text-warmth/70 text-sm font-light">·</span>
          <p className="hidden md:inline font-mono text-[0.62rem] tracking-[0.28em] uppercase text-warmth/85">
            {lang === "de" ? "30 Min · kostenfrei" : "30 min · complimentary"}
          </p>
        </div>
        <Link
          to="/kontakt#termin"
          data-testid="booking-ribbon-cta"
          className="flex items-center gap-2 px-5 md:px-7 py-2.5 md:py-3 bg-signal text-depth font-mono uppercase tracking-[0.28em] text-[0.62rem] md:text-[0.7rem] hover:bg-canvas hover:text-depth transition-colors duration-500"
        >
          {lang === "de" ? "Termin" : "Book"}
          <span aria-hidden>↗</span>
        </Link>
        <button
          type="button"
          onClick={dismiss}
          data-testid="booking-ribbon-dismiss"
          aria-label={lang === "de" ? "Leiste schließen" : "Dismiss"}
          className="px-3 md:px-4 text-warmth hover:text-canvas transition-colors text-lg leading-none border-l border-warmth/20"
        >
          ×
        </button>
      </div>
    </div>
  );
};

export default BookingRibbon;
